// frontend/src/components/admin/CompteRenduListe.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CompteRenduListe.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CompteRenduListe = () => {
  const navigate = useNavigate();
  
  const [comptesRendus, setComptesRendus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtres
  const [filtres, setFiltres] = useState({
    statut: '',
    annee: new Date().getFullYear().toString(),
    page: 1,
    limit: 10
  });
  
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1
  });

  // Statistiques
  const [stats, setStats] = useState(null);

  // Sélection de l'assemblée avant de créer un nouveau compte rendu
  const [showAssembleeModal, setShowAssembleeModal] = useState(false);
  const [assemblees, setAssemblees] = useState([]);
  const [assembleeChoisie, setAssembleeChoisie] = useState('');
  const [chargementAssemblees, setChargementAssemblees] = useState(false);

  useEffect(() => {
    chargerComptesRendus();
    chargerStatistiques();
  }, [filtres]);

  const ouvrirSelectionAssemblee = async () => {
    setShowAssembleeModal(true);
    setAssembleeChoisie('');
    setChargementAssemblees(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/assemblees?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const liste = response.data.data?.assemblees || response.data.data || [];
      setAssemblees(Array.isArray(liste) ? liste : []);
    } catch (err) {
      console.error('Erreur chargement assemblées:', err);
      setAssemblees([]);
    } finally {
      setChargementAssemblees(false);
    }
  };

  const confirmerNouveauCompteRendu = () => {
    setShowAssembleeModal(false);
    if (assembleeChoisie) {
      navigate(`/admin/comptes-rendus/nouveau?assemblee=${assembleeChoisie}`);
    } else {
      navigate('/admin/comptes-rendus/nouveau');
    }
  };

  const chargerComptesRendus = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filtres.statut) params.append('statut', filtres.statut);
      if (filtres.annee) params.append('annee', filtres.annee);
      params.append('page', filtres.page);
      params.append('limit', filtres.limit);

      const response = await axios.get(`${API_URL}/comptes-rendus?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setComptesRendus(response.data.data.comptes_rendus);
      setPagination(response.data.data.pagination);
    } catch (err) {
      console.error('Erreur chargement comptes rendus:', err);
      setError('Impossible de charger les comptes rendus');
    } finally {
      setLoading(false);
    }
  };

  const chargerStatistiques = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/comptes-rendus/statistiques?annee=${filtres.annee}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Erreur chargement statistiques:', err);
    }
  };

  const handleFiltreChange = (e) => {
    const { name, value } = e.target;
    setFiltres(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset à la page 1 quand on change un filtre
    }));
  };

  const changerPage = (nouvellePage) => {
    setFiltres(prev => ({
      ...prev,
      page: nouvellePage
    }));
  };

  const validerCompteRendu = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir valider ce compte rendu ? Cette action générera le PDF.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/comptes-rendus/${id}/valider`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Compte rendu validé avec succès!');
      chargerComptesRendus();
    } catch (err) {
      console.error('Erreur validation:', err);
      alert(err.response?.data?.message || 'Erreur lors de la validation');
    }
  };

  const telechargerPDF = async (id, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/comptes-rendus/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'compte-rendu.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Erreur téléchargement PDF:', err);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const supprimerCompteRendu = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce compte rendu ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/comptes-rendus/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Compte rendu supprimé avec succès!');
      chargerComptesRendus();
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      brouillon: { class: 'badge-draft', text: 'Brouillon' },
      en_attente: { class: 'badge-pending', text: 'En attente' },
      valide: { class: 'badge-valid', text: 'Validé' },
      archive: { class: 'badge-archive', text: 'Archivé' }
    };

    const badge = badges[statut] || badges.brouillon;
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const formaterDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Générer les années pour le filtre
  const annees = [];
  const anneeActuelle = new Date().getFullYear();
  for (let i = anneeActuelle; i >= 2020; i--) {
    annees.push(i);
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des comptes rendus...</p>
      </div>
    );
  }

  return (
    <div className="compte-rendu-liste">
      <div className="page-header">
        <h1>Comptes Rendus de Réunions</h1>
        <button type="button" onClick={ouvrirSelectionAssemblee} className="btn-primary">
          <span className="icon">+</span>
          Nouveau Compte Rendu
        </button>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="statistics-cards">
          <div className="stat-card">
            <div className="stat-icon">📋</div>
            <div className="stat-content">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>

          {stats.par_statut && stats.par_statut.map(stat => (
            <div className="stat-card" key={stat.statut}>
              <div className="stat-icon">
                {stat.statut === 'valide' ? '✓' :
                 stat.statut === 'brouillon' ? '📝' :
                 stat.statut === 'en_attente' ? '⏳' : '📦'}
              </div>
              <div className="stat-content">
                <div className="stat-value">{stat.count}</div>
                <div className="stat-label">
                  {stat.statut === 'valide' ? 'Validés' :
                   stat.statut === 'brouillon' ? 'Brouillons' :
                   stat.statut === 'en_attente' ? 'En attente' : 'Archivés'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div className="filtres">
        <div className="filtre-group">
          <label htmlFor="statut">Statut:</label>
          <select
            id="statut"
            name="statut"
            value={filtres.statut}
            onChange={handleFiltreChange}
          >
            <option value="">Tous</option>
            <option value="brouillon">Brouillon</option>
            <option value="en_attente">En attente</option>
            <option value="valide">Validé</option>
            <option value="archive">Archivé</option>
          </select>
        </div>

        <div className="filtre-group">
          <label htmlFor="annee">Année:</label>
          <select
            id="annee"
            name="annee"
            value={filtres.annee}
            onChange={handleFiltreChange}
          >
            <option value="">Toutes</option>
            {annees.map(annee => (
              <option key={annee} value={annee}>{annee}</option>
            ))}
          </select>
        </div>

        <button 
          className="btn-refresh" 
          onClick={chargerComptesRendus}
          title="Rafraîchir"
        >
          🔄
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Liste des comptes rendus */}
      {comptesRendus.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Aucun compte rendu</h3>
          <p>Créez votre premier compte rendu de réunion</p>
          <button type="button" onClick={ouvrirSelectionAssemblee} className="btn-primary">
            Créer un compte rendu
          </button>
        </div>
      ) : (
        <>
          <div className="comptes-rendus-grid">
            {comptesRendus.map(cr => (
              <div key={cr.id} className="compte-rendu-card">
                <div className="card-header">
                  <div className="card-date">
                    <div className="date-day">
                      {new Date(cr.date_reunion).getDate()}
                    </div>
                    <div className="date-month">
                      {new Date(cr.date_reunion).toLocaleDateString('fr-FR', { month: 'short' })}
                    </div>
                    <div className="date-year">
                      {new Date(cr.date_reunion).getFullYear()}
                    </div>
                  </div>

                  <div className="card-info">
                    <h3>{formaterDate(cr.date_reunion)}</h3>
                    <p className="card-lieu">
                      <span className="icon">📍</span>
                      {cr.lieu}
                    </p>
                    <p className="card-heure">
                      <span className="icon">🕐</span>
                      {cr.heure_prevue}
                      {cr.heure_debut_reelle && ` (début: ${cr.heure_debut_reelle})`}
                    </p>
                  </div>

                  {getStatutBadge(cr.statut)}
                </div>

                <div className="card-body">
                  <div className="card-stats">
                    <div className="stat-item">
                      <span className="stat-icon">👥</span>
                      <span>{cr.nombre_presents || 0} présents</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-icon">💰</span>
                      <span>
                        {(
                          (parseFloat(cr.total_cotisations_caisse) || 0) + 
                          (parseFloat(cr.total_cotisations_plats) || 0) + 
                          (parseFloat(cr.total_cotisations_maladie) || 0)
                        ).toLocaleString('fr-FR')} FCFA
                      </span>
                    </div>
                  </div>

                  {cr.redacteur && (
                    <p className="card-redacteur">
                      <span className="icon">✍️</span>
                      Rédigé par {cr.redacteur.nom} {cr.redacteur.prenom}
                    </p>
                  )}
                </div>

                <div className="card-actions">
                  <button
                    className="btn-action btn-view"
                    onClick={() => navigate(`/admin/comptes-rendus/${cr.id}`)}
                    title="Voir"
                  >
                    👁️
                  </button>

                  <button
                    className="btn-action btn-edit"
                    onClick={() => navigate(`/admin/comptes-rendus/modifier/${cr.id}`)}
                    title="Modifier"
                  >
                    ✏️
                  </button>

                  {cr.statut !== 'valide' && (
                    <button
                      className="btn-action btn-validate"
                      onClick={() => validerCompteRendu(cr.id)}
                      title="Valider"
                    >
                      ✓
                    </button>
                  )}

                  {cr.pdf_url && (
                    <button
                      className="btn-action btn-pdf"
                      onClick={() => telechargerPDF(cr.id, cr.pdf_filename)}
                      title="Télécharger PDF"
                    >
                      📄
                    </button>
                  )}

                  <button
                    className="btn-action btn-delete"
                    onClick={() => supprimerCompteRendu(cr.id)}
                    title="Supprimer"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn-pagination"
                onClick={() => changerPage(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                ← Précédent
              </button>

              <div className="pagination-info">
                Page {pagination.page} sur {pagination.pages}
                <span className="total-items">
                  ({pagination.total} compte{pagination.total > 1 ? 's' : ''} rendu{pagination.total > 1 ? 's' : ''})
                </span>
              </div>

              <button
                className="btn-pagination"
                onClick={() => changerPage(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}

      {showAssembleeModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setShowAssembleeModal(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: 8, padding: 24, width: 420, maxWidth: '90vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Pour quelle assemblée ?</h3>
            <p style={{ color: '#666', fontSize: '0.9em' }}>
              Lier le compte rendu à une assemblée permet de pré-remplir automatiquement les
              cotisations, la tontine, les emprunts, la dette et les comptes bancaires.
            </p>

            {chargementAssemblees ? (
              <p>Chargement des assemblées…</p>
            ) : (
              <select
                value={assembleeChoisie}
                onChange={(e) => setAssembleeChoisie(e.target.value)}
                style={{ width: '100%', padding: '8px', marginBottom: '16px' }}
              >
                <option value="">— Saisie libre (sans assemblée) —</option>
                {assemblees.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.date_assemblee ? new Date(a.date_assemblee).toLocaleDateString('fr-FR') : ''} — {a.lieu || 'Lieu non précisé'}
                  </option>
                ))}
              </select>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-secondary" onClick={() => setShowAssembleeModal(false)}>
                Annuler
              </button>
              <button type="button" className="btn-primary" onClick={confirmerNouveauCompteRendu}>
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompteRenduListe;
